import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api, { fmtOdds } from "../lib/api";
import { BookLinks, BOOK_SHORT, useBookLinks, trackBookClick } from "../components/BookLinks";
import { UpgradeGate } from "../components/Bits";
import NCAAFMatchup from "./NCAAFMatchup";
import NFLStats from "./NFLStats";
import LikelyCash from "./LikelyCash";
import FieldWeather from "./FieldWeather";
import Simulators from "./Simulators";
import PropsThatPay from "./PropsThatPay";
import TeamMatchup from "./TeamMatchup";
import { Clock, Tv, MapPin, Trophy, Flame, Target } from "lucide-react";

function Price({ odds, book, urls, testid }) {
  if (odds == null) return <span className="text-neutral-600">—</span>;
  const url = book ? urls[book] : null;
  const inner = <>{fmtOdds(odds)}{book && <sup className="text-[8px] text-neutral-500 ml-0.5 font-sans">{BOOK_SHORT[book] || book}</sup>}</>;
  if (!url) return <b className="text-neutral-300">{inner}</b>;
  return (
    <a href={url} target="_blank" rel="noreferrer" onClick={() => trackBookClick(book)} data-testid={testid}
      title={`Best price at ${book}`}
      className="font-bold text-neutral-200 underline decoration-dotted underline-offset-2 hover:text-[#a855f7] transition-colors duration-200">
      {inner}
    </a>
  );
}

function TeamRow({ t, right }) {
  return (
    <div className={`flex items-center gap-2 ${right ? "justify-end" : ""}`}>
      {!right && t.logo && <img src={t.logo} alt="" className="w-8 h-8 object-contain" loading="lazy" />}
      <div className={right ? "text-right" : ""}>
        <div className="font-display font-bold uppercase tracking-wide text-sm sm:text-base">{t.name}</div>
        {t.record && <div className="text-[10px] font-data text-neutral-500">{t.record}</div>}
      </div>
      {right && t.logo && <img src={t.logo} alt="" className="w-8 h-8 object-contain" loading="lazy" />}
    </div>
  );
}

function GameCard({ g, urls, i }) {
  const l = g.lines;
  const fmtSpread = (v) => (v == null ? null : v > 0 ? `+${v}` : `${v}`);
  return (
    <div className="card-surface rounded-xl p-4 hover-lift" data-testid={`nfl-game-${i}`}>
      <div className="flex items-center justify-between text-[11px] text-neutral-500 border-b border-white/10 pb-2 mb-3 flex-wrap gap-1">
        <span className="inline-flex items-center gap-1"><Clock size={12} className="text-[#a855f7]" /> {g.state === "pre" ? g.time_et : g.status}</span>
        {g.tv && <span className="inline-flex items-center gap-1"><Tv size={12} className="text-[#a855f7]" /> {g.tv}</span>}
        {g.venue && <span className="inline-flex items-center gap-1"><MapPin size={12} className="text-[#a855f7]" /> {g.venue}</span>}
      </div>
      <div className="flex items-center justify-between gap-2">
        <TeamRow t={g.away} />
        <span className="text-neutral-600 font-data text-xs shrink-0">@</span>
        <TeamRow t={g.home} right />
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3 text-center">
        <div className="bg-black/40 border border-white/10 rounded p-2">
          <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold">Spread</div>
          {l?.spread_home == null ? (
            <div className="font-data text-xs text-neutral-600 mt-2">Lines drop closer to game time</div>
          ) : (
            <div className="font-data text-xs sm:text-sm mt-1">
              <div>{g.away.abbrev} {fmtSpread(l.spread_away)} <Price odds={l.spread_away_price} book={l.spread_away_book} urls={urls} testid={`nfl-sp-away-${i}`} /></div>
              <div>{g.home.abbrev} {fmtSpread(l.spread_home)} <Price odds={l.spread_home_price} book={l.spread_home_book} urls={urls} testid={`nfl-sp-home-${i}`} /></div>
            </div>
          )}
        </div>
        <div className="bg-black/40 border border-white/10 rounded p-2">
          <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold">Moneyline</div>
          {l?.ml_home == null ? (
            <div className="font-data text-xs text-neutral-600 mt-2">—</div>
          ) : (
            <div className="font-data text-xs sm:text-sm mt-1">
              <div>{g.away.abbrev} <Price odds={l.ml_away} book={l.ml_away_book} urls={urls} testid={`nfl-ml-away-${i}`} /></div>
              <div>{g.home.abbrev} <Price odds={l.ml_home} book={l.ml_home_book} urls={urls} testid={`nfl-ml-home-${i}`} /></div>
              {l.ml_draw != null && <div className="text-neutral-400">DRAW <Price odds={l.ml_draw} book={l.ml_draw_book} urls={urls} testid={`nfl-ml-draw-${i}`} /></div>}
            </div>
          )}
        </div>
        <div className="bg-black/40 border border-white/10 rounded p-2">
          <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold">Total</div>
          {l?.total == null ? (
            <div className="font-data text-xs text-neutral-600 mt-2">—</div>
          ) : (
            <div className="font-data text-xs sm:text-sm mt-1">
              <div>O {l.total} <Price odds={l.over_price} book={l.over_book} urls={urls} testid={`nfl-ov-${i}`} /></div>
              <div>U {l.total} <Price odds={l.under_price} book={l.under_book} urls={urls} testid={`nfl-un-${i}`} /></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function SlateTab({ urls, league, endpoint }) {
  const { data, isLoading } = useQuery({ queryKey: [league, "board", endpoint], queryFn: () => api.get(endpoint || `/${league}/board`).then((r) => r.data), refetchInterval: 600000 });
  const days = data?.days || [];
  let idx = 0;
  return (
    <div className="space-y-6">
      {isLoading && <div className="text-neutral-500 font-data text-sm animate-pulse">Loading the slate...</div>}
      {!isLoading && days.length === 0 && (
        <div className="card-surface rounded-xl p-10 text-center text-neutral-400" data-testid="nfl-empty">No NFL games in the next few days. Check back soon.</div>
      )}
      {days.map((d) => (
        <div key={d.date} className="space-y-3" data-testid={`nfl-day-${d.date}`}>
          <h2 className="font-display text-lg font-bold uppercase tracking-wide text-[#a855f7] border-b border-[#a855f7]/20 pb-1">{d.label}</h2>
          <div className="grid lg:grid-cols-2 gap-4">
            {d.games.map((g) => <GameCard key={g.id} g={g} urls={urls} i={idx++} />)}
          </div>
        </div>
      ))}
      <div className="card-surface rounded-xl p-4">
        <BookLinks title="Shop the slate at your book" testid="nfl-book-links" />
      </div>
    </div>
  );
}

const CONF_STYLE = {
  high: "bg-[#22c55e]/20 text-[#22c55e] border-[#22c55e]/60",
  medium: "bg-[#a855f7]/15 text-[#a855f7] border-[#a855f7]/50",
  "coin-flip": "bg-white/10 text-neutral-400 border-white/20",
};

export function PredictionsTab({ league, endpoint }) {
  const { data, isLoading } = useQuery({ queryKey: [league, "predictions", endpoint], queryFn: () => api.get(endpoint || `/${league}/predictions`).then((r) => r.data), refetchInterval: 600000 });
  const games = data?.games || [];
  return (
    <div className="space-y-4">
      <p className="text-neutral-500 text-sm">Winner picks, projected scores and team totals — derived from live market consensus (the sharpest predictor there is), not gut feel. High confidence = 68%+ win probability.</p>
      {isLoading && <div className="text-neutral-500 font-data text-sm animate-pulse">Crunching the numbers...</div>}
      <div className="grid lg:grid-cols-2 gap-4">
        {games.map((g, i) => {
          const homePick = g.pick_team === g.home.name;
          return (
            <div key={g.game} className="card-surface rounded-xl p-4 hover-lift" data-testid={`nfl-pred-${i}`}>
              <div className="flex items-center justify-between text-[11px] text-neutral-500 border-b border-white/10 pb-2 mb-3">
                <span>{g.day_label} · {g.time_et}</span>
                <span className={`text-[9px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full border ${CONF_STYLE[g.confidence]}`} data-testid={`nfl-pred-conf-${i}`}>
                  {g.confidence === "coin-flip" ? "Coin Flip" : `${g.confidence} confidence`}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <div className={`flex items-center gap-2 ${!homePick ? "" : "opacity-60"}`}>
                  {g.away.logo && <img src={g.away.logo} alt="" className="w-9 h-9 object-contain" loading="lazy" />}
                  <div>
                    <div className="font-display font-bold uppercase text-sm">{g.away.name}</div>
                    <div className="font-data text-[11px] text-neutral-500">{g.win_pct_away}% win</div>
                  </div>
                </div>
                <div className="text-center shrink-0">
                  <div className="font-data text-xl font-black gold-text" data-testid={`nfl-proj-score-${i}`}>{g.proj_score}</div>
                  <div className="text-[9px] uppercase tracking-widest text-neutral-600 font-bold">projected</div>
                </div>
                <div className={`flex items-center gap-2 justify-end text-right ${homePick ? "" : "opacity-60"}`}>
                  <div>
                    <div className="font-display font-bold uppercase text-sm">{g.home.name}</div>
                    <div className="font-data text-[11px] text-neutral-500">{g.win_pct_home}% win</div>
                  </div>
                  {g.home.logo && <img src={g.home.logo} alt="" className="w-9 h-9 object-contain" loading="lazy" />}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                <div className="bg-black/40 border border-[#22c55e]/30 rounded p-2">
                  <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold flex items-center justify-center gap-1"><Trophy size={10} className="text-[#22c55e]" /> Pick</div>
                  <div className="font-data text-sm font-black text-[#22c55e] mt-1">{g.pick_abbrev} ({g.pick_pct}%)</div>
                </div>
                <div className="bg-black/40 border border-white/10 rounded p-2">
                  <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold">{g.away.abbrev} Team Total</div>
                  <div className="font-data text-sm font-bold mt-1">{g.proj_away} pts</div>
                </div>
                <div className="bg-black/40 border border-white/10 rounded p-2">
                  <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold">{g.home.abbrev} Team Total</div>
                  <div className="font-data text-sm font-bold mt-1">{g.proj_home} pts</div>
                </div>
              </div>
              {g.total != null && <div className="text-[10px] text-neutral-600 mt-2 text-center">Game total {g.total} · Spread {g.spread_home > 0 ? `+${g.spread_home}` : g.spread_home} ({g.home.abbrev}) · via {g.source_book}</div>}
            </div>
          );
        })}
      </div>
      {!isLoading && games.length === 0 && <div className="card-surface rounded-xl p-10 text-center text-neutral-400">Predictions appear once books post lines for the slate.</div>}
    </div>
  );
}

function TDTab() {
  const { data, isLoading } = useQuery({ queryKey: ["nflTD"], queryFn: () => api.get("/nfl/td-predictor").then((r) => r.data), staleTime: 10 * 60 * 1000 });
  const games = data?.games || [];
  const acc = data?.accuracy;
  const [game, setGame] = useState(null);
  useEffect(() => {
    if (!game && games.length > 0) setGame(games[0].game);
  }, [games, game]);
  const sel = games.find((g) => g.game === game);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-center">
        <p className="text-neutral-500 text-sm flex-1 min-w-[240px]">Anytime TD scorer model — only the <b className="text-white">3-5 sharpest plays per game</b> make the board: depth-chart role, team implied points and red-zone profile. <span className="text-[#a855f7] font-bold">HIGH</span> tier = strongest signals — every pick is auto-graded below.</p>
        <div className="card-gold border border-[#a855f7]/40 rounded-lg px-4 py-2 text-center" data-testid="td-accuracy">
          <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold">Model Record{acc?.date ? ` — ${acc.date}` : ""} <span className="text-neutral-600 normal-case">· resets daily</span></div>
          <div className="font-data text-sm font-black gold-text">
            {acc?.picks > 0 ? `${acc.wins}-${acc.picks - acc.wins} (${acc.pct}%)` : "Grading starts after tonight"}
          </div>
          {acc?.high_picks > 0 && <div className="text-[10px] font-data text-[#22c55e]">High tier: {acc.high_wins}-{acc.high_picks - acc.high_wins} ({acc.high_pct}%)</div>}
        </div>
      </div>
      {isLoading && <div className="text-neutral-500 font-data text-sm animate-pulse">Scouting the end zone (first load takes a moment)...</div>}
      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="td-game-chips">
        {games.map((g) => (
          <button key={g.game} onClick={() => setGame(g.game)}
            className={`shrink-0 text-xs font-bold uppercase tracking-wide px-3 py-2 rounded-lg border transition-colors duration-200 ${game === g.game ? "bg-[#8b1526] border-[#c22040] text-white" : "border-white/15 text-neutral-400 hover:border-[#c22040]/60 hover:text-white"}`}>
            {g.game}
          </button>
        ))}
      </div>
      {sel && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="td-detail">
          {sel.teams.map((t) => (
            <div key={t.team} className="card-surface rounded-xl overflow-hidden">
              <div className="bg-[#8b1526] border-b border-[#c22040]/40 px-3 py-2 flex items-center gap-2">
                {t.logo && <img src={t.logo} alt="" className="w-6 h-6 object-contain" />}
                <span className="text-xs font-black uppercase tracking-widest text-white">{t.team}</span>
                <span className="ml-auto text-[10px] text-red-100/80 font-data">implied {t.implied} pts</span>
              </div>
              {t.players.map((p) => (
                <div key={p.name} className={`flex items-center gap-2.5 px-3 py-2 border-b border-white/5 last:border-0 ${p.tier === "high" ? "bg-[#a855f7]/10" : ""}`} data-testid={`td-player-${p.name.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`}>
                  {p.headshot ? <img src={p.headshot} alt="" className="w-8 h-8 rounded-full object-cover bg-white/5" loading="lazy" /> : <span className="w-8 h-8 rounded-full bg-white/5 inline-flex items-center justify-center"><Target size={13} className="text-neutral-600" /></span>}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{p.name} {p.tier === "high" && <Flame size={12} className="inline text-[#a855f7]" />}</div>
                    <div className="text-[10px] text-neutral-500">{p.pos} · {p.exp === 0 ? "Rookie" : `Year ${p.exp + 1}`}</div>
                  </div>
                  <div className="text-right">
                    <div className={`font-data text-sm font-black ${p.tier === "high" ? "text-[#a855f7]" : "text-neutral-300"}`}>{p.prob}%</div>
                    <div className="text-[10px] font-data text-neutral-500">fair {p.fair_odds > 0 ? `+${p.fair_odds}` : p.fair_odds}</div>
                  </div>
                  {p.tier === "high" && <span className="text-[8px] uppercase tracking-widest font-black bg-[#a855f7] text-black px-1.5 py-0.5 rounded-full shrink-0">High</span>}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      {data?.locked && <UpgradeGate feature={`The full TD Predictor board for all ${data.total} games`} />}
      <p className="text-[11px] text-neutral-600">TD probabilities are model estimates — anytime TD is a high-variance market; fair odds show where the value line sits. Compare vs your book's price and only bet plus-value. Record above is fully transparent and auto-graded.</p>
    </div>
  );
}

const PROP_MARKETS_FOOT = [
  { key: "anytime_td", label: "Anytime TD" },
  { key: "pass_yds", label: "Passing Yds" },
  { key: "rush_yds", label: "Rushing Yds" },
  { key: "rec_yds", label: "Receiving Yds" },
  { key: "kick_pts", label: "Kicking Pts" },
  { key: "sacks", label: "Sacks" },
  { key: "tackles", label: "Tackles" },
  { key: "ints", label: "INTs Thrown" },
];
const PROP_MARKETS_BY_LEAGUE = {
  nfl: PROP_MARKETS_FOOT,
  ncaaf: PROP_MARKETS_FOOT,
  nba: [
    { key: "points", label: "Points" },
    { key: "rebounds", label: "Rebounds" },
    { key: "assists", label: "Assists" },
    { key: "threes", label: "3PT Made" },
    { key: "pra", label: "Pts+Reb+Ast" },
  ],
  nhl: [
    { key: "goal_anytime", label: "Anytime Goal" },
    { key: "points", label: "Points" },
    { key: "shots", label: "Shots on Goal" },
    { key: "assists", label: "Assists" },
    { key: "saves", label: "Goalie Saves" },
  ],
};

function PropsTab({ league }) {
  const PROP_MARKETS = PROP_MARKETS_BY_LEAGUE[league] || PROP_MARKETS_FOOT;
  const [market, setMarket] = useState("best");
  useEffect(() => { setMarket("best"); }, [league]);
  const { data: best, isLoading: bestLoading } = useQuery({
    queryKey: [league, "propsBest"],
    queryFn: () => api.get(`/props/best?league=${league}`).then((r) => r.data),
    staleTime: 30 * 60 * 1000,
    enabled: market === "best",
  });
  const { data, isLoading } = useQuery({ queryKey: [league, "props", market], queryFn: () => api.get(`/football/props?league=${league}&market=${market}`).then((r) => r.data), staleTime: 30 * 60 * 1000, enabled: market !== "best" });
  const games = data?.games || [];
  const ou = data?.ou;
  const [i, setI] = useState(0);
  const sel = games[Math.min(i, games.length - 1)];
  const fmtB = (v) => (v == null ? "—" : v > 0 ? `+${v}` : `${v}`);
  const bestBets = best?.bets || [];
  return (
    <div className="space-y-4">
      <p className="text-neutral-500 text-sm">Real prop prices from DraftKings, FanDuel, BetMGM & Caesars. <b className="text-white">Best Bets</b> shows only the specific props most likely to cash — or browse the full menus.</p>
      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="props-market-tabs">
        <button onClick={() => setMarket("best")} data-testid="props-market-best"
          className={`shrink-0 text-[11px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors duration-200 ${market === "best" ? "bg-[#22c55e] text-black border-[#22c55e]" : "border-[#22c55e]/40 text-[#22c55e] hover:bg-[#22c55e]/10"}`}>
          ★ Best Bets
        </button>
        {PROP_MARKETS.map((m) => (
          <button key={m.key} onClick={() => { setMarket(m.key); setI(0); }} data-testid={`props-market-${m.key}`}
            className={`shrink-0 text-[11px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors duration-200 ${market === m.key ? "bg-[#a855f7] text-black border-[#a855f7]" : "border-white/15 text-neutral-400 hover:border-[#a855f7]/60 hover:text-[#a855f7]"}`}>
            {m.label}
          </button>
        ))}
      </div>
      {market === "best" && (
        <>
          {bestLoading && <div className="text-neutral-500 font-data text-sm animate-pulse">Scanning every prop menu for the cashers...</div>}
          {!bestLoading && bestBets.length === 0 && <div className="card-surface rounded-xl p-10 text-center text-neutral-400" data-testid="props-best-empty">No props posted yet — books drop them closer to game time.</div>}
          <div className="space-y-2">
            {bestBets.map((b, idx) => (
              <div key={`${b.player}-${b.bet}`} className={`card-surface rounded-xl p-3.5 flex items-center gap-3 ${idx === 0 ? "border border-[#22c55e]/40" : ""}`} data-testid={`props-best-row-${idx}`}>
                <span className={`font-data text-sm font-black w-6 text-center shrink-0 ${idx < 3 ? "text-[#22c55e]" : "text-neutral-600"}`}>{idx + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold uppercase tracking-wide text-sm truncate">{b.player} <span className="text-[#a855f7]">{b.bet}</span></div>
                  <div className="text-[11px] text-neutral-500 truncate">{b.game}</div>
                </div>
                <div className="hidden sm:block w-28 shrink-0">
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full rounded-full bg-[#22c55e]" style={{ width: `${Math.min(b.prob, 100)}%` }} /></div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-data text-sm font-black text-[#22c55e]">{b.prob}%</div>
                  <div className="text-[10px] font-data text-neutral-500">{fmtB(b.odds)}{b.book ? ` · ${b.book}` : ""}</div>
                </div>
              </div>
            ))}
          </div>
          {bestBets.length > 0 && <p className="text-[11px] text-neutral-600">Only props clearing the probability bar make this list (implied % from real book prices, juice removed on over/unders). Stack 2-3 for a same game parlay.</p>}
        </>
      )}
      {market !== "best" && (
      <>
      {isLoading && <div className="text-neutral-500 font-data text-sm animate-pulse">Pulling {data?.market_label || "prop"} menus...</div>}
      {!isLoading && games.length === 0 && <div className="card-surface rounded-xl p-10 text-center text-neutral-400" data-testid="props-empty">Books haven't posted {PROP_MARKETS.find((m) => m.key === market)?.label} props yet — they drop closer to game time. Check back on game day.</div>}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {games.map((g, idx) => (
          <button key={g.game} onClick={() => setI(idx)}
            className={`shrink-0 text-xs font-bold uppercase tracking-wide px-3 py-2 rounded-lg border transition-colors duration-200 ${idx === i ? "bg-[#8b1526] border-[#c22040] text-white" : "border-white/15 text-neutral-400 hover:border-[#c22040]/60"}`}>
            {g.game}
          </button>
        ))}
      </div>
      {sel && !ou && (
        <div className="card-surface rounded-xl overflow-x-auto" data-testid="props-table">
          <table className="w-full text-sm min-w-[560px]">
            <thead><tr className="text-neutral-500 text-[11px] uppercase tracking-wider text-left border-b border-white/10">
              <th className="p-3">Player</th><th className="p-3">Best {data?.market_label || "Price"}</th><th className="p-3">Book</th><th className="p-3">Implied %</th><th className="p-3">All Books</th></tr></thead>
            <tbody className="font-data">
              {sel.players.map((p, idx) => (
                <tr key={p.player} className={`border-b border-white/5 ${idx < 3 ? "bg-[#a855f7]/5" : ""}`}>
                  <td className="p-3 font-sans font-semibold">{p.player}</td>
                  <td className="p-3 font-black text-[#22c55e]">{fmtB(p.best)}</td>
                  <td className="p-3 text-neutral-400">{p.best_book}</td>
                  <td className={`p-3 font-black ${p.prob >= 50 ? "text-[#22c55e]" : p.prob >= 35 ? "text-[#a855f7]" : "text-neutral-400"}`} data-testid={`props-prob-${idx}`}>{p.prob != null ? `${p.prob}%` : "—"}</td>
                  <td className="p-3 text-[11px] text-neutral-500">{Object.entries(p.books).map(([b, v]) => `${b.split(" ")[0]} ${v > 0 ? "+" : ""}${v}`).join(" · ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {sel && ou && (
        <div className="card-surface rounded-xl overflow-x-auto" data-testid="props-table">
          <table className="w-full text-sm min-w-[620px]">
            <thead><tr className="text-neutral-500 text-[11px] uppercase tracking-wider text-left border-b border-white/10">
              <th className="p-3">Player</th><th className="p-3">Line</th><th className="p-3">Best Over</th><th className="p-3">Best Under</th><th className="p-3">O% / U%</th><th className="p-3">All Books</th></tr></thead>
            <tbody className="font-data">
              {sel.players.map((p, idx) => (
                <tr key={p.player} className={`border-b border-white/5 ${idx < 3 ? "bg-[#a855f7]/5" : ""}`}>
                  <td className="p-3 font-sans font-semibold">{p.player}</td>
                  <td className="p-3 font-black gold-text">{p.line}</td>
                  <td className="p-3"><span className="font-black text-[#22c55e]">{fmtB(p.over)}</span>{p.over_book && <span className="text-[10px] text-neutral-500 ml-1">{p.over_book.split(" ")[0]}</span>}</td>
                  <td className="p-3"><span className="font-black text-[#22c55e]">{fmtB(p.under)}</span>{p.under_book && <span className="text-[10px] text-neutral-500 ml-1">{p.under_book.split(" ")[0]}</span>}</td>
                  <td className="p-3 whitespace-nowrap" data-testid={`props-prob-${idx}`}>
                    <span className={p.over_prob >= (p.under_prob ?? 0) ? "font-black text-[#22c55e]" : "text-neutral-500"}>{p.over_prob != null ? `${p.over_prob}%` : "—"}</span>
                    <span className="text-neutral-600 mx-1">/</span>
                    <span className={(p.under_prob ?? 0) > (p.over_prob ?? 0) ? "font-black text-[#22c55e]" : "text-neutral-500"}>{p.under_prob != null ? `${p.under_prob}%` : "—"}</span>
                  </td>
                  <td className="p-3 text-[11px] text-neutral-500 whitespace-nowrap">{Object.entries(p.books).map(([b, v]) => `${b.split(" ")[0]} ${v.line ?? "—"} o${fmtB(v.over)}/u${fmtB(v.under)}`).join(" · ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>
      )}
    </div>
  );
}

const TABS = [
  { key: "slate", label: "Slate & Lines" },
  { key: "predictions", label: "Game Predictions" },
  { key: "td", label: "TD Predictor" },
];

const LEAGUE_META = {
  nfl: { name: "NFL", fallback: "Preseason", desc: "The gridiron sheet: full slate with live lines, market-driven game predictions, and the anytime TD scorer model." },
  ncaaf: { name: "NCAAF", fallback: "Football", desc: "College football: the full slate with live lines and market-driven game predictions." },
  nba: { name: "NBA", fallback: "Preseason", desc: "The hardwood sheet: full slate with live lines, market-driven game predictions, player props and the day's most likely winners." },
  nhl: { name: "NHL", fallback: "Preseason", desc: "The ice sheet: full slate with live lines, market-driven game predictions, player props and the day's most likely winners." },
};

export default function NFL() {
  const { tab: tabParam } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const league = pathname.startsWith("/ncaaf") ? "ncaaf" : pathname.startsWith("/nba") ? "nba" : pathname.startsWith("/nhl") ? "nhl" : "nfl";
  const meta = LEAGUE_META[league];
  const tabs = league === "nfl"
    ? [...TABS, { key: "cash", label: "Likely to Cash" }, { key: "props", label: "Props" }, { key: "props-pay", label: "Props That Pay" }, { key: "matchup", label: "Matchup Lab" }, { key: "weather", label: "Field & Weather" }, { key: "sims", label: "Simulators" }, { key: "stats", label: "Player Stats" }]
    : league === "ncaaf"
    ? [...TABS.filter((t) => t.key !== "td"), { key: "cash", label: "Likely to Cash" }, { key: "matchup", label: "Matchup Lab" }, { key: "props", label: "Props" }, { key: "props-pay", label: "Props That Pay" }, { key: "weather", label: "Field & Weather" }, { key: "sims", label: "Simulators" }]
    : [...TABS.filter((t) => t.key !== "td"), { key: "cash", label: "Likely to Cash" }, { key: "props", label: "Props" }, { key: "props-pay", label: "Props That Pay" }, { key: "matchup", label: "Matchup Lab" }];
  const tab = tabs.some((t) => t.key === tabParam) ? tabParam : "slate";
  const setTab = (k) => navigate(k === "slate" ? `/${league}` : `/${league}/${k}`);
  const { data } = useQuery({ queryKey: [league, "board"], queryFn: () => api.get(`/${league}/board`).then((r) => r.data) });
  const { urls } = useBookLinks();
  return (
    <div className="space-y-5">
      <div className="fade-up">
        <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">{meta.name} <span className="gold-text">{data?.season || meta.fallback}</span></h1>
        <p className="text-neutral-500 text-sm mt-1">{meta.desc}</p>
      </div>
      <div className="flex gap-2 overflow-x-auto fade-up-1" data-testid="nfl-tabs">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`nfl-tab-${t.key}`}
            className={`shrink-0 text-xs font-black uppercase tracking-widest px-4 py-2 rounded-full border transition-colors duration-200 ${tab === t.key ? "bg-[#a855f7] text-black border-[#a855f7]" : "border-white/15 text-neutral-400 hover:border-[#a855f7]/60 hover:text-[#a855f7]"}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="fade-up-1">
        {tab === "slate" && <SlateTab urls={urls} league={league} />}
        {tab === "predictions" && <PredictionsTab league={league} />}
        {tab === "td" && league === "nfl" && <TDTab />}
        {tab === "matchup" && league === "ncaaf" && <NCAAFMatchup />}
        {tab === "matchup" && league !== "ncaaf" && <TeamMatchup league={league} />}
        {tab === "props" && <PropsTab league={league} />}
        {tab === "props-pay" && <PropsThatPay league={league} />}
        {tab === "cash" && <LikelyCash league={league} />}
        {tab === "weather" && <FieldWeather league={league} />}
        {tab === "sims" && <Simulators league={league} />}
        {tab === "stats" && league === "nfl" && <NFLStats />}
      </div>
      <p className="text-[11px] text-neutral-600">Schedule & broadcast info from ESPN. Odds from live sportsbooks. Verify at your book before betting.</p>
    </div>
  );
}
